import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import LeadForm from './LeadForm';
import LeadList from './LeadList';
import LeadStats from './LeadStats';
import SimpleLeadFilter from './SimpleLeadFilter';

import NotificationToast from './NotificationToast';
import FollowUpManager from './FollowUpManager';
import QuickFollowUp from './QuickFollowUp';
import FollowUpReminder from './FollowUpReminder';
import ReminderNotification from './ReminderNotification';
import FollowUpModal from './FollowUpModal';
import FollowUpHistory from './FollowUpHistory';
import AdvancedLeadStats from './AdvancedLeadStats';
import AdvancedLeadFilters from './AdvancedLeadFilters';
import LeadStatusManager from './LeadStatusManager';
import FormFieldManager from './FormFieldManager';
import NotificationCenter from './NotificationCenter';
import EmployeeManagementWithTabs from './EmployeeManagementWithTabs';
import BulkLeadAssignmentModal from './BulkLeadAssignmentModal';
import LeadsService from '../services/leadsService';
import FollowUpService from '../services/followUpService';
import notificationService from '../services/notificationService';
import LinkGenerator from './LinkGenerator';
import FieldManager from './FieldManager';
import { PlusIcon, ChartBarIcon, UsersIcon, XMarkIcon, CogIcon, CheckCircleIcon, ClockIcon, CurrencyDollarIcon, BellIcon, LinkIcon, DocumentTextIcon, CheckIcon } from '@heroicons/react/24/outline';
import { BellIcon as BellSolidIcon } from '@heroicons/react/24/solid';

const LeadManagement = ({ activeTab: externalActiveTab, setActiveTab: externalSetActiveTab }) => {
  const [leads, setLeads] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [filter, setFilter] = useState('all');
  const [assignmentFilter, setAssignmentFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [autoCapture, setAutoCapture] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const [notifications, setNotifications] = useState([]);
  const [showFollowUps, setShowFollowUps] = useState(false);

  // Use external activeTab if provided, otherwise use internal state
  const activeTab = externalActiveTab || 'dashboard';
  const setActiveTab = externalSetActiveTab || (() => { });
  const [showQuickFollowUp, setShowQuickFollowUp] = useState(false);
  const [selectedLeadForFollowUp, setSelectedLeadForFollowUp] = useState(null);
  const [reminders, setReminders] = useState([]);

  // New follow-up system state
  const [followUpModal, setFollowUpModal] = useState({ isOpen: false, lead: null });
  const [selectedLeadForHistory, setSelectedLeadForHistory] = useState(null);
  const [followUpRefresh, setFollowUpRefresh] = useState(0);
  const [upcomingFollowUps, setUpcomingFollowUps] = useState([]);
  const [overdueFollowUps, setOverdueFollowUps] = useState([]);

  // Advanced features state
  const [activeFilter, setActiveFilter] = useState('all');
  const [advancedFilters, setAdvancedFilters] = useState({});
  const [showStatusFlowConfig, setShowStatusFlowConfig] = useState(false);
  const [showFormFieldManager, setShowFormFieldManager] = useState(false);
  const [statusFlow, setStatusFlow] = useState([]);

  // Notification system state
  const [showNotificationCenter, setShowNotificationCenter] = useState(false);
  const [allNotifications, setAllNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Bulk assignment state
  const [showBulkAssignment, setShowBulkAssignment] = useState(false);
  const [selectedLeadsForBulkAssignment, setSelectedLeadsForBulkAssignment] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [simpleFilters, setSimpleFilters] = useState([]);

  // Bulk import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importProgress, setImportProgress] = useState({ total: 0, success: 0, failed: 0 });
  const [isImporting, setIsImporting] = useState(false);

  // Load leads from external API
  useEffect(() => {
    loadLeads();
    loadFollowUpData();
    loadLeadStatuses();
    loadEmployees();
  }, []);

  // Listen for lead form events from TopBar
  useEffect(() => {
    const handleOpenLeadForm = (event) => {
      if (event.detail?.action === 'create') {
        setEditingLead(null);
        setShowForm(true);
      }
    };

    window.addEventListener('openLeadForm', handleOpenLeadForm);
    return () => window.removeEventListener('openLeadForm', handleOpenLeadForm);
  }, []);

  // Debug log when leads change
  useEffect(() => {
    console.log('Leads updated:', leads);
  }, [leads]);

  // Handle edit from URL parameters
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && leads.length > 0) {
      const leadToEdit = leads.find(l => l._id === editId || l.id === editId);
      if (leadToEdit && !showForm && (!editingLead || (editingLead._id !== editId && editingLead.id !== editId))) {
        setEditingLead(leadToEdit);
        setShowForm(true);
      }
    }
  }, [searchParams, leads, showForm, editingLead]);

  // Auto-refresh leads every 30 seconds to keep data synchronized
  useEffect(() => {
    const interval = setInterval(() => {
      console.log('Auto-refreshing leads data...');
      loadLeads();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Initialize notification system
  useEffect(() => {
    // Load existing notifications
    const notifications = notificationService.getAllNotifications();
    setAllNotifications(notifications);
    setUnreadCount(notificationService.getUnreadCount());

    // Add listener for notification updates
    const handleNotificationUpdate = (updatedNotifications) => {
      setAllNotifications(updatedNotifications);
      setUnreadCount(notificationService.getUnreadCount());
    };

    notificationService.addListener(handleNotificationUpdate);

    // Request browser notification permission
    notificationService.requestNotificationPermission();

    return () => {
      notificationService.removeListener(handleNotificationUpdate);
    };
  }, []);

  // Check for overdue and upcoming follow-ups when leads change
  useEffect(() => {
    if (leads.length > 0) {
      // Check for overdue follow-ups
      notificationService.checkOverdueFollowUps(leads);
      // Check for upcoming follow-ups
      notificationService.checkUpcomingFollowUps(leads);
    }
  }, [leads]);

  const loadLeads = async () => {
    try {
      console.log('🔄 Loading leads...');
      const result = await LeadsService.getAllLeads();

      console.log('🔍 API Result:', result);
      console.log('🔍 Result success:', result.success);
      console.log('🔍 Result data:', result.data);
      console.log('🔍 Data length:', result.data?.length);

      if (result.success && result.data && result.data.length > 0) {
        console.log('✅ Leads loaded successfully:', result.data.length, 'leads');
        setLeads(result.data);

        // Clean up stale notifications for leads that no longer exist
        const removedCount = notificationService.cleanupStaleNotifications(result.data);
        if (removedCount > 0) {
          // Refresh notifications state to reflect cleanup
          const updatedNotifications = notificationService.getAllNotifications();
          setNotifications(updatedNotifications);
        }

        // Check for follow-ups and create notifications
        notificationService.checkOverdueFollowUps(result.data);
        notificationService.checkUpcomingFollowUps(result.data);
      } else {
        // No leads found — show empty state, do not use sample data
        console.log('ℹ️ No leads data received — showing empty state');
        setLeads([]);
      }
    } catch (error) {
      console.error('💥 Error loading leads:', error);
      // Show empty state when backend is unavailable
      setLeads([]);
    }
  };

  const handleAddLead = async (leadData) => {
    const result = await LeadsService.createLead(leadData);
    if (result.success) {
      await loadLeads(); // Reload leads from API
      setShowForm(false);
      addNotification('Lead created successfully!', 'success');
    } else {
      addNotification(`Failed to create lead: ${result.error}`, 'error');
    }
  };

  const handleEditLead = async (leadData) => {
    const leadId = editingLead.id || editingLead._id;
    if (!leadId) {
      addNotification('Error: Lead ID not found', 'error');
      return;
    }

    console.log('Updating lead with ID:', leadId);
    console.log('Lead data:', leadData);

    const result = await LeadsService.updateLead(leadId, leadData);
    console.log('Update result:', result);

    if (result.success) {
      // Update the lead in local state immediately
      setLeads(prevLeads =>
        prevLeads.map(lead =>
          (lead.id === leadId || lead._id === leadId)
            ? { ...lead, ...leadData, id: lead.id || lead._id }
            : lead
        )
      );

      await loadLeads(); // Reload leads from API

      // Clear edit state and URL parameter
      if (searchParams.get('edit')) {
        searchParams.delete('edit');
        setSearchParams(searchParams);
      }
      setEditingLead(null);
      setShowForm(false);

      addNotification('Lead updated successfully!', 'success');
    } else {
      addNotification(`Failed to update lead: ${result.error}`, 'error');
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      const result = await LeadsService.deleteLead(leadId);
      if (result.success) {
        await loadLeads(); // Reload leads from API

        // Force refresh notifications to remove deleted lead notifications
        const updatedNotifications = notificationService.getAllNotifications();
        setNotifications(updatedNotifications);

        addNotification('Lead deleted successfully!', 'success');
      } else {
        addNotification(`Failed to delete lead: ${result.error}`, 'error');
      }
    }
  };

  const handleStatusChange = async (leadId, newStatus, remark = '') => {
    const lead = leads.find(l => l._id === leadId);
    if (lead) {
      const updatedLead = {
        ...lead,
        status: newStatus,
        lastContact: new Date(),
        statusRemark: remark // Use statusRemark (not statusChangeRemark)
      };
      const result = await LeadsService.updateLead(leadId, updatedLead);
      if (result.success) {
        await loadLeads(); // Reload leads from API
        addNotification('Lead status updated!', 'success');
      } else {
        addNotification(`Failed to update status: ${result.error}`, 'error');
      }
    }
  };

  const getStatusOutcome = (statusName) => {
    if (!statusName || !leadStatuses) return '';
    const status = leadStatuses.find(s => s.name.toLowerCase() === statusName.toLowerCase());
    if (!status) {
      if (statusName.toLowerCase() === 'succesful') return 'converted';
      return '';
    }
    return status.outcome || '';
  };

  // Enhanced filtering logic
  const filteredLeads = (Array.isArray(leads) ? leads : []).filter(lead => {
    if (!lead) return false;

    // Basic filter (status/source) - Support both activeFilter for Analytics and filter for Dashboard
    let matchesFilter = true;
    const currentFilter = activeTab === 'analytics' ? activeFilter : filter;
    if (currentFilter && currentFilter !== 'all' && currentFilter !== '') {
      if (currentFilter.startsWith('source:')) {
        const sourceFilter = currentFilter.replace('source:', '');
        matchesFilter = lead.source === sourceFilter;
      } else if (currentFilter === 'follow') {
        const hasUpcoming = upcomingFollowUps.some(f => f.leadId === lead._id || f.lead === lead._id);
        const hasOverdue = overdueFollowUps.some(f => f.leadId === lead._id || f.lead === lead._id);
        matchesFilter = hasUpcoming || hasOverdue;
      } else if (currentFilter === 'closed_won') {
        matchesFilter = getStatusOutcome(lead.status) === 'converted';
      } else if (currentFilter === 'closed_lost') {
        matchesFilter = getStatusOutcome(lead.status) === 'rejected';
      } else {
        matchesFilter = lead.status === currentFilter;
      }
    }

    // Search filter
    const matchesSearch = !searchTerm ||
      (lead.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lead.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lead.company || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lead.assignedEmployeeName || '').toLowerCase().includes(searchTerm.toLowerCase());

    // Simple filters (from SimpleLeadFilter component)
    let matchesSimpleFilters = true;
    if (simpleFilters.length > 0) {
      simpleFilters.forEach(filter => {
        switch (filter.type) {
          case 'employee':
            // Check if lead is assigned to this employee
            const assignedEmployees = lead.assignedEmployees || [];
            const assignedTo = lead.assignedTo;
            matchesSimpleFilters = matchesSimpleFilters && (
              assignedEmployees.includes(filter.value) ||
              assignedTo === filter.value ||
              (lead.assignedEmployeeName && lead.assignedEmployeeName.toLowerCase().includes(filter.valueLabel.toLowerCase()))
            );
            break;
          case 'title':
            matchesSimpleFilters = matchesSimpleFilters && (lead.customFields?.title || lead.title) === filter.value;
            break;
          case 'status':
            matchesSimpleFilters = matchesSimpleFilters && lead.status === filter.value;
            break;
          case 'source':
            matchesSimpleFilters = matchesSimpleFilters && lead.source === filter.value;
            break;
          case 'date':
            const leadDate = new Date(lead.createdAt);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            switch (filter.value) {
              case 'today':
                matchesSimpleFilters = matchesSimpleFilters && leadDate.toDateString() === today.toDateString();
                break;
              case 'yesterday':
                const yesterday = new Date(today);
                yesterday.setDate(yesterday.getDate() - 1);
                matchesSimpleFilters = matchesSimpleFilters && leadDate.toDateString() === yesterday.toDateString();
                break;
              case 'last7days':
                const last7days = new Date(today);
                last7days.setDate(last7days.getDate() - 7);
                matchesSimpleFilters = matchesSimpleFilters && leadDate >= last7days;
                break;
              case 'last30days':
                const last30days = new Date(today);
                last30days.setDate(last30days.getDate() - 30);
                matchesSimpleFilters = matchesSimpleFilters && leadDate >= last30days;
                break;
              case 'thisMonth':
                matchesSimpleFilters = matchesSimpleFilters &&
                  leadDate.getMonth() === today.getMonth() &&
                  leadDate.getFullYear() === today.getFullYear();
                break;
              case 'lastMonth':
                const lastMonth = new Date(today);
                lastMonth.setMonth(lastMonth.getMonth() - 1);
                matchesSimpleFilters = matchesSimpleFilters &&
                  leadDate.getMonth() === lastMonth.getMonth() &&
                  leadDate.getFullYear() === lastMonth.getFullYear();
                break;
            }
            break;
        }
      });
    }

    // Advanced filters
    let matchesAdvanced = true;
    if (Object.keys(advancedFilters).length > 0) {
      // Status filter
      if (advancedFilters.status && advancedFilters.status.length > 0) {
        matchesAdvanced = matchesAdvanced && advancedFilters.status.includes(lead.status);
      }

      // Source filter
      if (advancedFilters.source && advancedFilters.source.length > 0) {
        matchesAdvanced = matchesAdvanced && advancedFilters.source.includes(lead.source);
      }

      // Priority filter
      if (advancedFilters.priority && advancedFilters.priority.length > 0) {
        matchesAdvanced = matchesAdvanced && advancedFilters.priority.includes(lead.priority || 'medium');
      }

      // Date range filter
      if (advancedFilters.dateRange && (advancedFilters.dateRange.start || advancedFilters.dateRange.end)) {
        const leadDate = new Date(lead.createdAt);
        if (advancedFilters.dateRange.start) {
          matchesAdvanced = matchesAdvanced && leadDate >= new Date(advancedFilters.dateRange.start);
        }
        if (advancedFilters.dateRange.end) {
          matchesAdvanced = matchesAdvanced && leadDate <= new Date(advancedFilters.dateRange.end);
        }
      }

      // Value range filter
      if (advancedFilters.valueRange && (advancedFilters.valueRange.min || advancedFilters.valueRange.max)) {
        const leadValue = lead.value || 0;
        if (advancedFilters.valueRange.min) {
          matchesAdvanced = matchesAdvanced && leadValue >= parseFloat(advancedFilters.valueRange.min);
        }
        if (advancedFilters.valueRange.max) {
          matchesAdvanced = matchesAdvanced && leadValue <= parseFloat(advancedFilters.valueRange.max);
        }
      }
    }

    return matchesFilter && matchesSearch && matchesSimpleFilters && matchesAdvanced;
  }).filter(lead => {
    if (assignmentFilter === 'all') return true;
    const isAssigned = !!(
      lead.assignedEmployeeName || 
      (lead.assignedEmployees && lead.assignedEmployees.length > 0) || 
      (lead.assignedTo && (Array.isArray(lead.assignedTo) ? lead.assignedTo.length > 0 : true))
    );
    if (assignmentFilter === 'assigned') return isAssigned;
    if (assignmentFilter === 'unassigned') return !isAssigned;
    return true;
  });

  const startEdit = (lead) => {
    setEditingLead(lead);
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingLead(null);
    if (searchParams.get('edit')) {
      searchParams.delete('edit');
      setSearchParams(searchParams);
    }
  };

  // Advanced features handlers
  const handleStatsFilter = (filterType) => {
    setActiveFilter(filterType);
    setAdvancedFilters({}); // Clear advanced filters when using stats filter
  };

  const handleAdvancedFilter = (filters) => {
    setAdvancedFilters(filters);
    setActiveFilter('all'); // Reset basic filter when using advanced filters
  };

  const handleSimpleFilterChange = (filters) => {
    setSimpleFilters(filters);
  };

  const handleStatusFlowSave = (newStatusFlow) => {
    setStatusFlow(newStatusFlow);
    // Here you could save to backend/localStorage
    localStorage.setItem('leadStatusFlow', JSON.stringify(newStatusFlow));
  };

  // Load status flow from localStorage on component mount
  useEffect(() => {
    const savedFlow = localStorage.getItem('leadStatusFlow');
    if (savedFlow) {
      setStatusFlow(JSON.parse(savedFlow));
    }
  }, []);

  const handleAutoLead = async (leadData) => {
    // Use the same API call as manual lead creation
    const result = await LeadsService.createLead(leadData);
    if (result.success) {
      await loadLeads(); // Reload leads from API

      // Add notification for auto-captured lead
      const notification = {
        id: Date.now(),
        lead: leadData,
        timestamp: new Date()
      };
      setNotifications(prev => [...prev, notification]);

      // Auto remove notification after 5 seconds
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
      }, 5000);
    } else {
      console.error('Failed to auto-capture lead:', result.error);
    }
  };

  const removeNotification = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const addNotification = (message, type = 'info') => {
    const notification = {
      id: Date.now(),
      message,
      type,
      timestamp: new Date()
    };
    setNotifications(prev => [...prev, notification]);

    // Auto remove notification after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 5000);
  };

  const handleScheduleFollowUp = (lead) => {
    setSelectedLeadForFollowUp(lead);
    setShowQuickFollowUp(true);
  };

  // Notification Center handlers
  const handleNotificationClick = () => {
    setShowNotificationCenter(true);
  };

  const handleCloseNotificationCenter = () => {
    setShowNotificationCenter(false);
  };

  const handleMarkAsRead = (notificationId) => {
    notificationService.markAsRead(notificationId);
  };

  const handleMarkAllAsRead = () => {
    notificationService.markAllAsRead();
  };

  const handleDeleteNotification = (notificationId) => {
    notificationService.deleteNotification(notificationId);
  };

  const handleQuickFollowUpSchedule = (followUpData) => {
    // Save to localStorage (same as FollowUpManager)
    const existingFollowUps = JSON.parse(localStorage.getItem('crm-followups') || '[]');
    const updatedFollowUps = [...existingFollowUps, followUpData];
    localStorage.setItem('crm-followups', JSON.stringify(updatedFollowUps));

    // Show notification
    const notification = {
      id: Date.now(),
      lead: selectedLeadForFollowUp,
      timestamp: new Date(),
      type: 'followup'
    };
    setNotifications(prev => [...prev, notification]);

    // Auto remove notification after 3 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 3000);

    setShowQuickFollowUp(false);
    setSelectedLeadForFollowUp(null);
  };

  const handleReminder = (reminderData) => {
    setReminders(prev => [...prev, reminderData]);
  };

  const dismissReminder = (reminderId) => {
    setReminders(prev => prev.filter(r => r.id !== reminderId));
  };

  const markFollowUpComplete = (followUpId) => {
    const followUps = JSON.parse(localStorage.getItem('crm-followups') || '[]');
    const updatedFollowUps = followUps.map(f =>
      f.id === followUpId ? { ...f, status: 'completed' } : f
    );
    localStorage.setItem('crm-followups', JSON.stringify(updatedFollowUps));

    // Remove related reminders
    setReminders(prev => prev.filter(r => r.followUpId !== followUpId));
  };

  const handleAssignmentComplete = async (result) => {
    // Reload leads to reflect assignment changes
    await loadLeads();

    // Show success notification with detailed message
    if (result.success) {
      addNotification(result.message || 'Lead assigned successfully!', 'success');
    }
  };

  // New follow-up system handlers
  const handleAddFollowUp = (lead) => {
    setFollowUpModal({ isOpen: true, lead });
  };

  const handleFollowUpAdded = () => {
    setFollowUpRefresh(prev => prev + 1);
    loadFollowUpData();
    addNotification('Follow-up added successfully!', 'success');
  };

  const handleViewFollowUpHistory = (lead) => {
    setSelectedLeadForHistory(lead);
  };

  const closeFollowUpModal = () => {
    setFollowUpModal({ isOpen: false, lead: null });
  };

  const closeFollowUpHistory = () => {
    setSelectedLeadForHistory(null);
  };

  // Bulk assignment handlers
  const handleBulkAssignment = (selectedLeads) => {
    if (selectedLeads.length === 0) {
      addNotification('Please select at least one lead for bulk assignment', 'warning');
      return;
    }
    setSelectedLeadsForBulkAssignment(selectedLeads);
    setShowBulkAssignment(true);
  };

  const handleBulkAssignmentComplete = async (result) => {
    if (result.success) {
      addNotification(result.message || 'Leads assigned successfully!', 'success');
      await loadLeads(); // Refresh leads data
      setSelectedLeadsForBulkAssignment([]);
    } else {
      addNotification(result.error || 'Failed to assign leads', 'error');
    }
  };

  const closeBulkAssignment = () => {
    setShowBulkAssignment(false);
    setSelectedLeadsForBulkAssignment([]);
  };

  // Load follow-up data
  const loadFollowUpData = async () => {
    try {
      // Load upcoming follow-ups
      const upcomingResult = await FollowUpService.getUpcomingFollowUps();
      if (upcomingResult.success) {
        setUpcomingFollowUps(upcomingResult.data);
      }

      // Load overdue follow-ups
      const overdueResult = await FollowUpService.getOverdueFollowUps();
      if (overdueResult.success) {
        setOverdueFollowUps(overdueResult.data);
      }

      // Load employee follow-ups data
      const EmployeeDashboardService = require('../services/employeeDashboardService').default;
      const employeeResult = await EmployeeDashboardService.getAssignedLeads();
      if (employeeResult.success && employeeResult.data) {
        const employeeFollowUps = [];
        employeeResult.data.forEach(lead => {
          if (lead.followUps && lead.followUps.length > 0) {
            lead.followUps.forEach(followUp => {
              const followUpDate = new Date(followUp.nextFollowUpDate || followUp.createdAt);
              const today = new Date();
              today.setHours(0, 0, 0, 0);

              const followUpData = {
                ...followUp,
                leadId: lead._id,
                leadName: lead.name,
                leadCompany: lead.company,
                scheduledDate: followUp.nextFollowUpDate || followUp.createdAt
              };

              employeeFollowUps.push(followUpData);
            });
          }
        });
      }
    } catch (error) {
      console.error('Error loading follow-up data:', error);
    }
  };

  const loadLeadStatuses = async () => {
    try {
      const { leadsApi } = await import('../config/api');
      const response = await leadsApi.get('/lead-statuses');
      if (response.data.success) {
        setLeadStatuses(response.data.statuses);
      }
    } catch (error) {
      console.error('Error loading lead statuses:', error);
    }
  };

  const loadEmployees = async () => {
    try {
      const { leadsApi } = await import('../config/api');
      const response = await leadsApi.get('/employees');
      if (response.data.success) {
        setEmployees(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF('landscape');

    // Add title
    doc.setFontSize(18);
    doc.text('Leads Report', 14, 15);

    // Add date
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 14, 22);
    doc.text(`Total Leads: ${filteredLeads.length}`, 14, 27);

    // Prepare table data
    const tableData = filteredLeads.map(lead => [
      lead.name || '-',
      lead.email || '-',
      lead.phone || '-',
      lead.company || '-',
      lead.status || '-',
      lead.source || '-',
      lead.value ? `₹${lead.value.toLocaleString('en-IN')}` : '-',
      lead.assignedEmployeeName || '-',
      lead.createdAt ? new Date(lead.createdAt).toLocaleDateString('en-IN') : '-'
    ]);

    // Add table
    autoTable(doc, {
      head: [['Name', 'Email', 'Phone', 'Company', 'Status', 'Source', 'Value', 'Assigned To', 'Created']],
      body: tableData,
      startY: 32,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [59, 130, 246], fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 35 },
        2: { cellWidth: 25 },
        3: { cellWidth: 30 },
        4: { cellWidth: 20 },
        5: { cellWidth: 20 },
        6: { cellWidth: 25 },
        7: { cellWidth: 25 },
        8: { cellWidth: 22 }
      }
    });

    // Save PDF
    doc.save(`leads-report-${new Date().toISOString().split('T')[0]}.pdf`);
    addNotification('PDF exported successfully!', 'success');
  };

  // Export to Excel
  const exportToExcel = () => {
    const excelData = filteredLeads.map(lead => ({
      'Name': lead.name || '-',
      'Email': lead.email || '-',
      'Phone': lead.phone || '-',
      'Company': lead.company || '-',
      'Title': lead.customFields?.title || lead.title || '-',
      'Status': lead.status || '-',
      'Source': lead.source || '-',
      'Value': lead.value || 0,
      'Budget': lead.budget || '-',
      'Assigned To': lead.assignedEmployeeName || '-',
      'Notes': lead.notes || '-',
      'Created Date': lead.createdAt ? new Date(lead.createdAt).toLocaleDateString('en-IN') : '-',
      'Last Contact': lead.lastContact ? new Date(lead.lastContact).toLocaleDateString('en-IN') : '-'
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Leads');

    // Save Excel file
    XLSX.writeFile(workbook, `leads-report-${new Date().toISOString().split('T')[0]}.xlsx`);
    addNotification('Excel exported successfully!', 'success');
  };

  // Download Excel Template
  const downloadTemplate = () => {
    const templateData = [{
      'Name': 'John Doe',
      'Email': 'john@example.com',
      'Phone': '9876543210',
      'Company': 'ABC Corp',
      'Title': 'Website Development',
      'Source': 'website',
      'Budget': '₹1,00,000',
      'Notes': 'Interested in web development services',
      'Status': 'new'
    }];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Leads Template');

    // Add instructions sheet
    const instructions = [
      ['Bulk Lead Import Template Instructions'],
      [''],
      ['Required Fields:', 'Name, Email (or Phone - at least one contact method required)'],
      ['Optional Fields:', 'Phone, Company, Title, Source, Budget, Notes, Status'],
      [''],
      ['Field Guidelines:'],
      ['Name', 'Full name of the lead'],
      ['Email', 'Valid email address'],
      ['Phone', 'Valid 10-digit phone number'],
      ['Company', 'Company name'],
      ['Title', 'Lead purpose/title (e.g., Website Development, Mobile App)'],
      ['Source', 'Lead source: website, instagram, facebook, whatsapp, linkedin, referral, etc.'],
      ['Budget', 'Budget in format: ₹1,00,000 or ₹50,000 - ₹1,00,000'],
      ['Notes', 'Any additional notes'],
      ['Status', 'Lead status (default: new). Use existing status names only.'],
      [''],
      ['Important Notes:'],
      ['• All imported leads will have the status specified in the Status column (default: new)'],
      ['• Assignment will be done manually after import'],
      ['• Duplicate emails will be skipped'],
      ['• Invalid phone numbers will be flagged but import will continue'],
      ['• Delete this instruction sheet and the example row before importing']
    ];

    const instructionSheet = XLSX.utils.aoa_to_sheet(instructions);
    XLSX.utils.book_append_sheet(workbook, instructionSheet, 'Instructions');

    XLSX.writeFile(workbook, 'lead-import-template.xlsx');
    addNotification('Template downloaded successfully!', 'success');
  };

  // Handle Import File Selection
  const handleImportFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        setImportFile(file);
      } else {
        addNotification('Please select a valid Excel file (.xlsx or .xls)', 'error');
      }
    }
  };

  // Import Leads from Excel
  const importLeadsFromExcel = async () => {
    if (!importFile) {
      addNotification('Please select a file to import', 'error');
      return;
    }

    setIsImporting(true);
    setImportProgress({ total: 0, success: 0, failed: 0 });

    try {
      const data = await importFile.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        addNotification('No data found in the Excel file', 'error');
        setIsImporting(false);
        return;
      }

      setImportProgress({ total: jsonData.length, success: 0, failed: 0 });

      let successCount = 0;
      let failedCount = 0;

      for (const row of jsonData) {
        try {
          // Validate required fields
          if (!row.Name || (!row.Email && !row.Phone)) {
            console.warn('Skipping row - missing required fields:', row);
            failedCount++;
            continue;
          }

          // Prepare lead data
          const leadData = {
            name: row.Name,
            email: row.Email?.toLowerCase() || '',
            phone: row.Phone || '',
            company: row.Company || '',
            source: row.Source?.toLowerCase() || 'website',
            budget: row.Budget || '',
            notes: row.Notes || '',
            status: row.Status?.toLowerCase() || 'new',
            priority: 'medium',
            value: 0,
            customFields: {
              title: row.Title || ''
            }
          };

          // Create lead
          const result = await LeadsService.createLead(leadData);

          if (result.success) {
            successCount++;
          } else {
            failedCount++;
          }

          setImportProgress({ total: jsonData.length, success: successCount, failed: failedCount });
        } catch (error) {
          console.error('Error importing row:', error);
          failedCount++;
          setImportProgress({ total: jsonData.length, success: successCount, failed: failedCount });
        }
      }

      // Reload leads
      await loadLeads();

      addNotification(
        `Import completed! Success: ${successCount}, Failed: ${failedCount}`,
        successCount > 0 ? 'success' : 'error'
      );

      // Close modal and reset
      setShowImportModal(false);
      setImportFile(null);
    } catch (error) {
      console.error('Error importing leads:', error);
      addNotification('Failed to import leads. Please check the file format.', 'error');
    } finally {
      setIsImporting(false);
    }
  };

  // Calculate total lead value from budget field
  const totalLeadValue = filteredLeads.reduce((sum, lead) => {
    if (!lead.budget) return sum;

    // Parse budget string (e.g., "₹1,00,000 - ₹2,50,000" or "₹75,000")
    const budgetStr = lead.budget.toString();

    // Remove ₹ symbol and commas
    const cleanStr = budgetStr.replace(/₹|,/g, '').trim();

    // Check if it's a range (contains -)
    if (cleanStr.includes('-')) {
      // Take the first value from range
      const firstValue = cleanStr.split('-')[0].trim();
      const parsed = parseFloat(firstValue);
      return sum + (isNaN(parsed) ? 0 : parsed);
    } else {
      // Single value
      const parsed = parseFloat(cleanStr);
      return sum + (isNaN(parsed) ? 0 : parsed);
    }
  }, 0);

  console.log('📊 Total Lead Value Calculation:', {
    filteredLeadsCount: filteredLeads.length,
    totalValue: totalLeadValue,
    sampleBudgets: filteredLeads.slice(0, 5).map(l => ({
      name: l.name,
      budget: l.budget
    }))
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
      <div className="px-2 py-2 md:px-2 md:py-2">

        {/* Conditional Content Based on Active Tab */}
        {activeTab === 'dashboard' && (
          <>
            {/* Quick Stats Overview — responsive scroll on mobile, row on desktop */}
            <div className="mb-4">
              {/* Mobile: 2x2 grid + scroll */}
              <div className="grid grid-cols-2 gap-2 md:hidden">
                {/* Total */}
                <div
                  onClick={() => setFilter('all')}
                  className={`bg-white border-2 rounded-xl px-3 py-2.5 cursor-pointer hover:shadow-md transition-all ${
                    filter === 'all' ? 'border-blue-500 shadow-md' : 'border-gray-200'
                  }`}
                >
                  <p className="text-gray-500 text-[10px] font-semibold uppercase tracking-wide mb-0.5">Total</p>
                  <p className="text-2xl font-extrabold text-gray-900">{leads.length}</p>
                </div>

                {/* Follow-ups */}
                <div
                  onClick={() => setFilter('follow')}
                  className={`bg-white border-2 rounded-xl px-3 py-2.5 cursor-pointer hover:shadow-md transition-all ${
                    filter === 'follow' ? 'border-blue-500 shadow-md' : 'border-gray-200'
                  }`}
                >
                  <p className="text-gray-500 text-[10px] font-semibold uppercase tracking-wide mb-0.5">Follow-ups</p>
                  <p className="text-2xl font-extrabold text-gray-900">{upcomingFollowUps.length + overdueFollowUps.length}</p>
                </div>

                {/* Dynamic Status Cards - mobile grid */}
                {leadStatuses.map((status) => {
                  const count = leads.filter(l => l.status === status.name).length;
                  const isActive = filter === status.name;
                  return (
                    <div
                      key={status._id}
                      onClick={() => setFilter(status.name)}
                      className={`bg-white border-2 rounded-xl px-3 py-2.5 cursor-pointer hover:shadow-md transition-all ${
                        isActive ? 'border-blue-500 shadow-md' : 'border-gray-200'
                      }`}
                    >
                      <p className="text-gray-500 text-[10px] font-semibold uppercase tracking-wide mb-0.5 truncate">{status.label}</p>
                      <p className="text-2xl font-extrabold text-gray-900">{count}</p>
                    </div>
                  );
                })}
              </div>

              {/* Desktop: horizontal scroll row */}
              <div className="hidden md:flex gap-2 overflow-x-auto pb-2">
                <div
                  onClick={() => setFilter('all')}
                  className={`bg-white border-2 rounded-lg px-4 py-2 min-w-[140px] flex-shrink-0 cursor-pointer hover:shadow-md transition-all ${
                    filter === 'all' ? 'border-blue-500 shadow-md' : 'border-gray-200'
                  }`}
                >
                  <p className="text-gray-600 text-xs mb-0.5 font-medium">TOTAL</p>
                  <p className="text-2xl font-bold text-gray-900">{leads.length}</p>
                </div>

                {leadStatuses.map((status) => {
                  const count = leads.filter(l => l.status === status.name).length;
                  const isActive = filter === status.name;
                  return (
                    <div
                      key={status._id}
                      onClick={() => setFilter(status.name)}
                      className={`bg-white border-2 rounded-lg px-4 py-2 min-w-[140px] flex-shrink-0 cursor-pointer hover:shadow-md transition-all ${
                        isActive ? 'border-blue-500 shadow-md' : 'border-gray-200'
                      }`}
                    >
                      <p className="text-gray-600 text-xs mb-0.5 uppercase font-medium">{status.label}</p>
                      <p className="text-2xl font-bold text-gray-900">{count}</p>
                    </div>
                  );
                })}

                <div
                  className={`bg-white border-2 rounded-lg px-4 py-2 min-w-[140px] flex-shrink-0 cursor-pointer hover:shadow-md transition-all ${
                    filter === 'follow' ? 'border-blue-500 shadow-md' : 'border-gray-200'
                  }`}
                  onClick={() => setFilter('follow')}
                >
                  <p className="text-gray-600 text-xs mb-0.5 uppercase font-medium">FOLLOW</p>
                  <p className="text-2xl font-bold text-gray-900">{upcomingFollowUps.length + overdueFollowUps.length}</p>
                </div>
              </div>
            </div>

            {/* Main Lead Management Section */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100">
              {/* Compact Filter Section - Mobile responsive */}
              <div className="border-b border-gray-200 px-3 py-2.5">
                {/* Mobile layout: stacked */}
                <div className="flex flex-col gap-2 md:hidden">
                  {/* Row 1: Search */}
                  <div className="relative w-full">
                    <input
                      type="text"
                      placeholder="Search leads..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-8 pr-2 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>

                  {/* Row 2: Status + Assignment dropdowns */}
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      style={{ fontSize: '13px' }}
                      className="w-full px-2 py-2 border border-gray-300 rounded-lg bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-gray-700 truncate"
                    >
                      <option value="all">All Status</option>
                      {leadStatuses.map(status => (
                        <option key={status._id} value={status.name}>{status.label}</option>
                      ))}
                      <option value="follow">Follow-ups</option>
                    </select>

                    <select
                      value={assignmentFilter}
                      onChange={(e) => setAssignmentFilter(e.target.value)}
                      style={{ fontSize: '13px' }}
                      className="w-full px-2 py-2 border border-gray-300 rounded-lg bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-gray-700 truncate"
                    >
                      <option value="all">All Leads</option>
                      <option value="assigned">Assigned</option>
                      <option value="unassigned">Unassigned</option>
                    </select>
                  </div>

                  {/* Row 3: Advanced Filter (SimpleLeadFilter handles its own 2-col layout on mobile) */}
                  <SimpleLeadFilter
                    onFilterChange={handleSimpleFilterChange}
                    leads={leads}
                    employees={employees}
                  />

                  {/* Row 4a: Lead Value Badge (full width) — light blue themed */}
                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 w-full">
                    <span>💰</span>
                    <span className="font-bold">
                      {totalLeadValue > 0 ? `₹${totalLeadValue.toLocaleString('en-IN')}` : '₹0'}
                    </span>
                    <span className="text-white/70">({filteredLeads.length} leads)</span>
                  </div>

                  {/* Row 4b: Action buttons — equal 4-col grid, uniform blue */}
                  <div className="grid grid-cols-4 gap-1.5">
                    <button
                      onClick={exportToPDF}
                      className="bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg text-xs font-medium transition-colors flex flex-col items-center justify-center gap-0.5"
                    >
                      <DocumentTextIcon className="h-4 w-4" />
                      <span>PDF</span>
                    </button>
                    <button
                      onClick={exportToExcel}
                      className="bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg text-xs font-medium transition-colors flex flex-col items-center justify-center gap-0.5"
                    >
                      <ChartBarIcon className="h-4 w-4" />
                      <span>Excel</span>
                    </button>
                    <button
                      onClick={() => setShowImportModal(true)}
                      className="bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg text-xs font-medium transition-colors flex flex-col items-center justify-center gap-0.5"
                    >
                      <PlusIcon className="h-4 w-4" />
                      <span>Import</span>
                    </button>
                    <button
                      onClick={() => setBulkSelectMode(!bulkSelectMode)}
                      className={`py-2 rounded-lg text-xs font-medium transition-colors flex flex-col items-center justify-center gap-0.5 ${
                        bulkSelectMode
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-blue-500 text-white hover:bg-blue-600'
                      }`}
                    >
                      <CheckIcon className="h-4 w-4" />
                      <span>{bulkSelectMode ? 'Exit' : 'Select'}</span>
                    </button>
                  </div>
                </div>

                {/* Desktop layout: original single row */}
                <div className="hidden md:flex items-center gap-2 flex-wrap">
                  {/* Search Box - Compact */}
                  <div className="relative min-w-[120px] flex-1 max-w-[220px]">
                    <input
                      type="text"
                      placeholder="Search leads..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>

                  {/* Status Filter - Compact */}
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Status</option>
                    {leadStatuses.map(status => (
                      <option key={status._id} value={status.name}>{status.label}</option>
                    ))}
                    <option value="follow">Follow-ups</option>
                  </select>

                  {/* Assignment Filter */}
                  <select
                    value={assignmentFilter}
                    onChange={(e) => setAssignmentFilter(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Leads</option>
                    <option value="assigned">✅ Assigned</option>
                    <option value="unassigned">⚠️ Unassigned</option>
                  </select>

                  {/* Simple Lead Filter Component */}
                  <SimpleLeadFilter
                    onFilterChange={handleSimpleFilterChange}
                    leads={leads}
                    employees={employees}
                  />

                  {/* Bulk Select Button */}
                  <button
                    onClick={() => setBulkSelectMode(!bulkSelectMode)}
                    className={`px-3 py-1.5 text-sm rounded font-medium transition-colors flex items-center gap-1 ${
                      bulkSelectMode
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <CheckIcon className="h-4 w-4" />
                    {bulkSelectMode ? 'Exit Select' : 'Bulk Select'}
                  </button>

                  {/* Total Lead Value + Export Buttons */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className={`${
                      totalLeadValue > 0 ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gray-400'
                    } text-white px-2 py-1.5 rounded text-xs font-semibold flex items-center gap-1 whitespace-nowrap`}>
                      <span>💰</span>
                      <span className="font-bold">
                        {totalLeadValue > 0 ? `₹${totalLeadValue.toLocaleString('en-IN')}` : '₹0'}
                      </span>
                      <span className="text-white/80">({filteredLeads.length})</span>
                    </div>

                    {/* Export Buttons */}
                    <button
                      onClick={exportToPDF}
                      className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1"
                    >
                      <DocumentTextIcon className="h-4 w-4" />
                      PDF
                    </button>
                    <button
                      onClick={exportToExcel}
                      className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1"
                    >
                      📊 Excel
                    </button>

                    {/* Import Button */}
                    <button
                      onClick={() => setShowImportModal(true)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1"
                    >
                      📥 Import
                    </button>
                  </div>
                </div>
              </div>

              {/* Lead List Section - Compact */}
              <div id="leads-table-section" className="p-4">
                <LeadList
                  leads={filteredLeads}
                  onEdit={startEdit}
                  onDelete={handleDeleteLead}
                  onStatusChange={handleStatusChange}
                  onFollowUp={handleAddFollowUp}
                  onViewHistory={handleViewFollowUpHistory}
                  onBulkAssignment={handleBulkAssignment}
                  onAssignmentComplete={handleAssignmentComplete}
                  bulkSelectMode={bulkSelectMode}
                  setBulkSelectMode={setBulkSelectMode}
                />
              </div>
            </div>
          </>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <>
            {/* Advanced Analytics Dashboard */}
            <AdvancedLeadStats
              leads={leads}
              onFilterChange={handleStatsFilter}
              activeFilter={activeFilter}
            />

            {/* Filtered Results */}
            {(activeFilter !== 'all' || (activeFilter === 'all' && filteredLeads.length > 0)) && (
              <div className="mt-6">
                <div className="bg-white rounded-xl shadow-lg border border-gray-100">
                  <div className="border-b border-gray-200 p-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                      {(!activeFilter || activeFilter === 'all' || activeFilter === '') ? 'All Leads' : `Filtered Results: ${activeFilter.replace('source:', '').replace('_', ' ').toUpperCase()}`}
                    </h3>
                    <p className="text-gray-600 text-sm">Showing {filteredLeads.length} leads</p>
                  </div>
                  <div id="analytics-leads-table-section" className="p-4">
                    <LeadList
                      leads={filteredLeads}
                      onEdit={startEdit}
                      onDelete={handleDeleteLead}
                      onStatusChange={handleStatusChange}
                      onFollowUp={handleAddFollowUp}
                      onViewHistory={handleViewFollowUpHistory}
                      onBulkAssignment={handleBulkAssignment}
                      onAssignmentComplete={handleAssignmentComplete}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Follow-Up Manager Tab */}
        {activeTab === 'followups' && (
          <FollowUpManager
            leads={leads}
            onUpdateLead={(leadData) => {
              setLeads(leads.map(lead =>
                lead.id === leadData.id ? leadData : lead
              ));
            }}
          />
        )}

        {/* Employee Management Tab */}
        {activeTab === 'employees' && (
          <EmployeeManagementWithTabs />
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-3 md:space-y-4">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl md:rounded-2xl shadow-xl px-4 py-4 md:p-6 text-white">
              <h2 className="text-lg md:text-3xl font-bold mb-1 md:mb-2 flex items-center gap-2">
                <CogIcon className="h-5 w-5 md:h-8 md:w-8 flex-shrink-0" />
                Lead Management Settings
              </h2>
              <p className="text-blue-100 text-xs md:text-sm">Configure and customize your lead management system</p>
            </div>

            {/* Core Configuration Section */}
            <div className="bg-white rounded-xl md:rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6">
              <h3 className="text-base md:text-xl font-bold text-gray-900 mb-3 md:mb-4 flex items-center">
                <span className="bg-blue-100 text-blue-600 rounded-lg p-1.5 md:p-2 mr-2 md:mr-3 text-base md:text-xl">🎯</span>
                Core Configuration
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                {/* Status Flow Configuration */}
                <div className="border border-gray-200 rounded-xl p-4 md:p-5 hover:shadow-md hover:border-blue-300 transition-all bg-white">
                  <div className="flex items-start mb-3 md:mb-4">
                    <div className="flex-1">
                      <div className="flex items-center mb-1.5 md:mb-2">
                        <span className="text-xl md:text-2xl mr-2">📊</span>
                        <h4 className="text-base md:text-lg font-semibold text-gray-900">Lead Status Flow</h4>
                      </div>
                      <p className="text-xs md:text-sm text-gray-600">Configure lead status workflow and transitions</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowStatusFlowConfig(true)}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg transition-colors font-medium text-sm shadow-sm hover:shadow-md"
                  >
                    Manage Statuses
                  </button>
                </div>

                {/* Form Field Manager */}
                <div className="border border-gray-200 rounded-xl p-4 md:p-5 hover:shadow-md hover:border-blue-300 transition-all bg-white">
                  <div className="flex items-start mb-3 md:mb-4">
                    <div className="flex-1">
                      <div className="flex items-center mb-1.5 md:mb-2">
                        <span className="text-xl md:text-2xl mr-2">📝</span>
                        <h4 className="text-base md:text-lg font-semibold text-gray-900">Form Fields</h4>
                      </div>
                      <p className="text-xs md:text-sm text-gray-600">Customize lead form fields and validation</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowFormFieldManager(true)}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg transition-colors font-medium text-sm shadow-sm hover:shadow-md"
                  >
                    Manage Fields
                  </button>
                </div>
              </div>
            </div>

            {/* Advanced Tools Section */}
            <div className="bg-white rounded-xl md:rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6">
              <h3 className="text-base md:text-xl font-bold text-gray-900 mb-3 md:mb-4 flex items-center">
                <span className="bg-blue-100 text-blue-600 rounded-lg p-1.5 md:p-2 mr-2 md:mr-3 text-base md:text-xl">🛠️</span>
                Advanced Tools
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                {/* Link Generator */}
                <div className="border border-gray-200 rounded-xl p-4 md:p-5 hover:shadow-md hover:border-blue-300 transition-all bg-white">
                  <div className="flex items-start mb-3 md:mb-4">
                    <div className="flex-1">
                      <div className="flex items-center mb-1.5 md:mb-2">
                        <span className="text-xl md:text-2xl mr-2">🔗</span>
                        <h4 className="text-base md:text-lg font-semibold text-gray-900">Link Generator</h4>
                      </div>
                      <p className="text-xs md:text-sm text-gray-600">Generate custom lead capture links</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setActiveTab('linkgenerator')}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg transition-colors font-medium text-sm shadow-sm hover:shadow-md"
                  >
                    Open Link Generator
                  </button>
                </div>

                {/* Field Manager */}
                <div className="border border-gray-200 rounded-xl p-4 md:p-5 hover:shadow-md hover:border-blue-300 transition-all bg-white">
                  <div className="flex items-start mb-1.5 md:mb-4">
                    <div className="flex-1">
                      <div className="flex items-center mb-1.5 md:mb-2">
                        <span className="text-xl md:text-2xl mr-2">⚡</span>
                        <h4 className="text-base md:text-lg font-semibold text-gray-900">Field Manager</h4>
                      </div>
                      <p className="text-xs md:text-sm text-gray-600">Advanced field configuration and settings</p>
                    </div>
                  </div>
                  <div className="mt-2 md:mt-3">
                    <FieldManager />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Link Generator Tab */}
        {activeTab === 'linkgenerator' && (
          <LinkGenerator />
        )}

      </div>

      {/* Lead Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <LeadForm
              lead={editingLead}
              onSubmit={editingLead ? handleEditLead : handleAddLead}
              onCancel={cancelForm}
            />
          </div>
        </div>
      )}

      {/* Quick Follow-Up Modal */}
      {showQuickFollowUp && selectedLeadForFollowUp && (
        <QuickFollowUp
          lead={selectedLeadForFollowUp}
          onSchedule={handleQuickFollowUpSchedule}
          onClose={() => {
            setShowQuickFollowUp(false);
            setSelectedLeadForFollowUp(null);
          }}
        />
      )}



      {/* Notification Toasts */}
      <NotificationToast
        notifications={notifications}
        onRemove={removeNotification}
      />

      {/* Follow-Up Reminder System */}
      <FollowUpReminder onReminder={handleReminder} />

      {/* Reminder Notifications */}
      <ReminderNotification
        reminders={reminders}
        onDismiss={dismissReminder}
        onMarkComplete={markFollowUpComplete}
      />

      {/* New Follow-up Modal */}
      <FollowUpModal
        isOpen={followUpModal.isOpen}
        onClose={closeFollowUpModal}
        lead={followUpModal.lead}
        onFollowUpAdded={handleFollowUpAdded}
      />

      {/* Follow-up History Modal */}
      {selectedLeadForHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Follow-up History</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Lead: {selectedLeadForHistory.name} • {selectedLeadForHistory.company || 'No Company'}
                </p>
              </div>
              <button
                onClick={closeFollowUpHistory}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <FollowUpHistory
                leadId={selectedLeadForHistory._id}
                refreshTrigger={followUpRefresh}
              />
            </div>
          </div>
        </div>
      )}

      {/* Status Management Modal */}
      {showStatusFlowConfig && (
        <LeadStatusManager
          isOpen={showStatusFlowConfig}
          onStatusesUpdated={handleStatusFlowSave}
          onClose={() => setShowStatusFlowConfig(false)}
        />
      )}

      {/* Form Field Manager Modal */}
      {showFormFieldManager && (
        <FormFieldManager
          isOpen={showFormFieldManager}
          onClose={() => setShowFormFieldManager(false)}
        />
      )}

      {/* Notification Center Modal */}
      {showNotificationCenter && (
        <NotificationCenter
          isOpen={showNotificationCenter}
          onClose={handleCloseNotificationCenter}
          notifications={allNotifications}
          onMarkAsRead={handleMarkAsRead}
          onMarkAllAsRead={handleMarkAllAsRead}
          onDeleteNotification={handleDeleteNotification}
        />
      )}

      {/* Bulk Lead Assignment Modal */}
      {showBulkAssignment && (
        <BulkLeadAssignmentModal
          isOpen={showBulkAssignment}
          onClose={closeBulkAssignment}
          selectedLeads={selectedLeadsForBulkAssignment}
          onAssignmentComplete={handleBulkAssignmentComplete}
        />
      )}

      {/* Import Leads Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 rounded-t-xl shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-2 rounded-lg">
                    <span className="text-2xl">📥</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Import Leads from Excel</h3>
                    <p className="text-blue-100 text-sm">Bulk upload leads using Excel file</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportFile(null);
                    setImportProgress({ total: 0, success: 0, failed: 0 });
                  }}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <span>📋</span> How to Import Leads
                </h4>
                <ol className="text-sm text-blue-800 space-y-1 ml-5 list-decimal">
                  <li>Download the Excel template below</li>
                  <li>Fill in your lead data following the template format</li>
                  <li>Save the file and upload it here</li>
                  <li>All leads will be imported with the status specified in the file (default: new)</li>
                  <li>You can assign leads to employees after import</li>
                </ol>
              </div>

              {/* Download Template Button */}
              <div className="flex justify-center">
                <button
                  onClick={downloadTemplate}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 shadow-md hover:shadow-lg"
                >
                  <span className="text-xl">📄</span>
                  Download Excel Template
                </button>
              </div>

              {/* File Upload */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleImportFileChange}
                  className="hidden"
                  id="import-file-input"
                  disabled={isImporting}
                />
                <label
                  htmlFor="import-file-input"
                  className="cursor-pointer flex flex-col items-center gap-3"
                >
                  <div className="bg-blue-100 p-4 rounded-full">
                    <span className="text-4xl">📂</span>
                  </div>
                  <div>
                    <p className="text-lg font-medium text-gray-900">
                      {importFile ? importFile.name : 'Click to select Excel file'}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Supports .xlsx and .xls files
                    </p>
                  </div>
                </label>
              </div>

              {/* Import Progress */}
              {isImporting && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">Importing leads...</span>
                    <span className="text-sm text-gray-600">
                      {importProgress.success + importProgress.failed} / {importProgress.total}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${((importProgress.success + importProgress.failed) / importProgress.total) * 100}%`
                      }}
                    />
                  </div>
                  <div className="flex gap-4 text-sm">
                    <span className="text-green-600 font-medium">✓ Success: {importProgress.success}</span>
                    <span className="text-red-600 font-medium">✗ Failed: {importProgress.failed}</span>
                  </div>
                </div>
              )}

              {/* Important Notes */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-900 mb-2 flex items-center gap-2">
                  <span>⚠️</span> Important Notes
                </h4>
                <ul className="text-sm text-yellow-800 space-y-1 ml-5 list-disc">
                  <li>Required fields: Name, Email (or Phone)</li>
                  <li>All imported leads will have the status from the Excel file (default: new)</li>
                  <li>Lead assignment should be done manually after import</li>
                  <li>Duplicate emails will be skipped automatically</li>
                  <li>Invalid data will be flagged but import will continue</li>
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 rounded-b-xl flex justify-end gap-3 border-t border-gray-200 shrink-0">
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                  setImportProgress({ total: 0, success: 0, failed: 0 });
                }}
                disabled={isImporting}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={importLeadsFromExcel}
                disabled={!importFile || isImporting}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all font-medium shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isImporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Importing...
                  </>
                ) : (
                  <>
                    <span>📥</span>
                    Import Leads
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadManagement;
